// Libs
import classNames from 'classnames/bind';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { FormNoteJob } from '~/components/module/job';
import { NoteItem } from '~/components/module/note';
import { BaseToolbar, ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
} from '~/utils/constants/common';
import { INote, IQueryGetListNote } from '~/utils/interface/note';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deleteNote, getListNote } from '~/thunks/notes/notesThunk';
import { LoadingSizeEnum, ParamsBusinessTypeEnum } from '~/utils/enum/common';
import { notesActions, selectIsRefreshNote } from '~/thunks/notes/notesSlice';
import { LoadingContext } from '~/context';
// Styles, images, icons
import styles from './JobNote.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const JobNote = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshNote = useSelector(selectIsRefreshNote);
  const loadingContext = useContext(LoadingContext);
  //#endregion Selector

  //#region Declare State
  const [isShowFormNote, setIsShowFormNote] = useState<boolean>(false);
  const [noteSelected, setNoteSelected] = useState<INote | null>(null);
  const [notes, setNotes] = useState<INote[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowConfirmDeleteNote, setIsShowConfirmDeleteNote] = useState<boolean>(false);

  const [hasMoreNote, setHasMoreNote] = useState<boolean>(false);
  const [currentPageNote, setCurrentPageNote] = useState<number>(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    const params: IQueryGetListNote = {
      page: currentPageNote,
      limit: DEFAULT_LIMIT_PAGE,
      businessId: jobId,
      businessType: ParamsBusinessTypeEnum.JOB,
    };

    handleGetListNoteJob(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, currentPageNote]);

  useEffect(() => {
    if (!isRefreshNote || !jobId) return;

    const params: IQueryGetListNote = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      businessId: jobId,
      businessType: ParamsBusinessTypeEnum.JOB,
    };

    handleGetListNoteJob(params);
    dispatch(notesActions.setIsRefreshNote(false));

    setCurrentPageNote(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshNote]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListNoteJob = (params: IQueryGetListNote) => {
    if (currentPageNote === BASE_PAGINATION_DEFAULT_CURRENT_PAGE && !isRefreshNote) {
      setIsLoading(true);
    }

    dispatch(getListNote(params))
      .unwrap()
      .then((res) => {
        const data = res?.data;

        if (!data || !data?.responses) {
          setHasMoreNote(false);

          return;
        }

        if (data?.pagination?.page === BASE_PAGINATION_DEFAULT_CURRENT_PAGE) {
          setNotes(data?.responses ?? []);
          setHasMoreNote(data?.pagination?.totalPages > BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
        } else {
          setNotes((prevState) => {
            const newData = data?.responses?.filter(
              (note) => !prevState?.some((oldNote) => oldNote?.id === note?.id)
            );

            const updateListNote = [...prevState, ...newData];

            setHasMoreNote(data?.pagination?.totalPages > BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

            return updateListNote;
          });
        }
      })
      .catch((error) => {
        setHasMoreNote(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseFormNote = () => {
    setIsShowFormNote(false);
    setNoteSelected(null);
  };

  const handleAddNote = () => {
    setIsShowFormNote(true);
  };

  const handleEditNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowFormNote(true);
  };

  const handleDeleteNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowConfirmDeleteNote(true);
  };

  const handleLoadMoreNote = () => {
    setCurrentPageNote((prev) => prev + DEFAULT_NUMBER_ONE);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDeleteNote(false);
    setNoteSelected(null);
  };

  const handleConfirmDelete = () => {
    if (!noteSelected?.id) return;

    loadingContext?.show();

    dispatch(deleteNote(noteSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(notesActions.setIsRefreshNote(true));
      })
      .catch((error) => {})
      .finally(() => {
        loadingContext?.hide();
        handleCloseConfirmDelete();
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='jobNoteTab' className={cx('container')}>
      <BaseToolbar textAction={t('form_note_job_btn_add')} onAction={handleAddNote}></BaseToolbar>

      {!isLoading && (
        <Box component={'div'} id='scrollableNotes' className={cx('listNoteContainer')}>
          <InfiniteScroll
            dataLength={notes.length}
            next={handleLoadMoreNote}
            hasMore={hasMoreNote}
            loader={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                <CircularProgress size={LoadingSizeEnum.MEDIUM} />
              </Stack>
            }
            scrollableTarget={'mainContentSection'}
            // {...(notes?.length > DEFAULT_NUMBER_ZERO &&
            //   hasMoreNote && {
            //     endMessage: notes?.length === DEFAULT_NUMBER_ZERO && (
            //       <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
            //         <Typography component={'p'} className={cx('noDataAvailable')}>
            //           {t('common_no_data', { ns: 'common/base' })}
            //         </Typography>
            //       </Stack>
            //     ),
            //   })}
          >
            <Box id='scrollableNotes' component={'div'} className={cx('listNote')}>
              {Array.isArray(notes) && notes?.length > DEFAULT_NUMBER_ZERO ? (
                notes?.map((note) => (
                  <NoteItem note={note} key={note?.id} onEdit={handleEditNote} onDelete={handleDeleteNote} />
                ))
              ) : (
                <Typography component={'span'} className={cx('noDataAvailable')}>
                  {t('common_no_data_available', { ns: 'common/base' })}
                </Typography>
              )}
            </Box>
          </InfiniteScroll>
        </Box>
      )}

      {isShowFormNote && (
        <FormNoteJob {...(noteSelected && { note: noteSelected })} onClose={handleCloseFormNote} />
      )}

      {isShowConfirmDeleteNote && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete_note', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default JobNote;
